import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { mapItemsApi, notificationsApi, usersApi, departmentsApi, calloutsApi } from '../lib/api';
import { useSocket } from '../context/SocketContext';
import EmergencyMap from '../components/map/EmergencyMap';
import LoadingScreen from '../components/common/LoadingScreen';
import EmptyState from '../components/common/EmptyState';
import TimeAgo from '../components/common/TimeAgo';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
  Map, 
  Plus, 
  Bell, 
  Trash2, 
  Edit, 
  Power,
  AlertTriangle,
  Construction,
  PartyPopper,
  Ban,
  Loader2,
  Send,
  Users,
  Building2,
  UserPlus,
  Shield,
  User,
  Mail,
  Megaphone,
  Check,
  Radio,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const AdminPage = () => {
  const { socket } = useSocket();
  const { user } = useAuth();
  const [mapItems, setMapItems] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [callouts, setCallouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('map-items');

  // Check if user is Dispatch personnel or Admin
  const isDispatch = user?.role === 'personnel' && user?.department === 'Dispatch';
  const isAdmin = user?.role === 'admin';
  const canManageUsers = isAdmin; // Only admins can manage users/departments

  // Helper function to extract error message from API responses
  const getErrorMessage = (error, defaultMsg = 'An error occurred') => {
    const detail = error?.response?.data?.detail;
    if (!detail) return defaultMsg;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      // Pydantic validation errors come as an array
      return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    if (typeof detail === 'object') {
      return detail.msg || detail.message || JSON.stringify(detail);
    }
    return defaultMsg;
  };
  
  // Map Item Form
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    title: '',
    description: '',
    item_type: 'road_closure',
    coordinates: '',
    radius: 200,
    is_active: true,
  });
  const [savingItem, setSavingItem] = useState(false);

  // Notification Form
  const [notifDialogOpen, setNotifDialogOpen] = useState(false);
  const [notifForm, setNotifForm] = useState({
    title: '',
    message: '',
    priority: 'medium',
  });
  const [savingNotif, setSavingNotif] = useState(false);

  // User Form
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    role: 'personnel',
    department: '',
  });
  const [savingUser, setSavingUser] = useState(false);

  // Department Form
  const [deptDialogOpen, setDeptDialogOpen] = useState(false);
  const [deptForm, setDeptForm] = useState({
    name: '',
    description: '',
  });
  const [savingDept, setSavingDept] = useState(false);

  // Callout Form
  const [calloutDialogOpen, setCalloutDialogOpen] = useState(false);
  const [calloutForm, setCalloutForm] = useState({
    department_id: '',
    title: '',
    message: '',
    priority: 'high',
  });
  const [savingCallout, setSavingCallout] = useState(false);

  const fetchData = async () => {
    try {
      const [itemsRes, notifsRes, usersRes, deptsRes, calloutsRes] = await Promise.all([
        mapItemsApi.getAll(),
        notificationsApi.getAll(),
        usersApi.getAll(),
        departmentsApi.getAll(),
        calloutsApi.getAll().catch(() => ({ data: [] })),
      ]);
      setMapItems(itemsRes.data);
      setNotifications(notifsRes.data);
      setUsers(usersRes.data);
      setDepartments(deptsRes.data);
      setCallouts(calloutsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (socket) {
      socket.on('map_item_created', (item) => {
        setMapItems(prev => [...prev, item]);
      });
      socket.on('map_item_updated', (item) => {
        setMapItems(prev => prev.map(i => i.id === item.id ? item : i));
      });
      socket.on('map_item_deleted', ({ id }) => {
        setMapItems(prev => prev.filter(i => i.id !== id));
      });
      socket.on('map_item_toggled', (item) => {
        setMapItems(prev => prev.map(i => i.id === item.id ? item : i));
      });

      return () => {
        socket.off('map_item_created');
        socket.off('map_item_updated');
        socket.off('map_item_deleted');
        socket.off('map_item_toggled');
      };
    }
  }, [socket]);

  const parseCoordinates = (coordString) => {
    try {
      // Normalize the input: replace newlines with semicolons, handle multiple spaces
      const normalized = coordString
        .replace(/\r?\n/g, ';')  // Replace newlines with semicolons
        .replace(/\s+/g, ' ')     // Normalize whitespace
        .replace(/;\s*;/g, ';')   // Remove empty entries
        .trim();
      
      const pairs = normalized.split(';').map(pair => {
        const trimmed = pair.trim();
        if (!trimmed) return null;
        
        // Split by comma and parse
        const parts = trimmed.split(',').map(n => parseFloat(n.trim()));
        if (parts.length >= 2) {
          return { lat: parts[0], lng: parts[1] };
        }
        return null;
      });
      
      return pairs.filter(p => p && !isNaN(p.lat) && !isNaN(p.lng));
    } catch (e) {
      console.error('Error parsing coordinates:', e);
      return [];
    }
  };

  const formatCoordinates = (coords) => {
    return coords.map(c => `${c.lat}, ${c.lng}`).join('; ');
  };

  const handleCreateItem = async () => {
    const coords = parseCoordinates(itemForm.coordinates);
    if (coords.length === 0) {
      toast.error('Please enter valid coordinates (format: lat, lng; lat, lng)');
      return;
    }

    const isClosedArea = itemForm.item_type === 'closed_area';
    if (isClosedArea) {
      const r = parseFloat(itemForm.radius);
      if (isNaN(r) || r <= 0) {
        toast.error('Please enter a valid radius (in meters) greater than 0');
        return;
      }
    }

    setSavingItem(true);
    try {
      const data = {
        title: itemForm.title,
        description: itemForm.description,
        item_type: itemForm.item_type,
        // Circular closed area uses a single center point + radius
        coordinates: isClosedArea ? [coords[0]] : coords,
        radius: isClosedArea ? parseFloat(itemForm.radius) : null,
        is_active: itemForm.is_active,
      };

      if (editingItem) {
        await mapItemsApi.update(editingItem.id, data);
        toast.success('Map item updated');
      } else {
        await mapItemsApi.create(data);
        toast.success('Map item created');
      }

      setItemDialogOpen(false);
      resetItemForm();
      fetchData();
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error(getErrorMessage(error, 'Failed to save map item'));
    } finally {
      setSavingItem(false);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      title: item.title,
      description: item.description,
      item_type: item.item_type,
      coordinates: formatCoordinates(item.coordinates),
      radius: item.radius || 200,
      is_active: item.is_active,
    });
    setItemDialogOpen(true);
  };

  const handleToggleItem = async (item) => {
    try {
      await mapItemsApi.toggle(item.id);
      toast.success(`${item.title} ${item.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      console.error('Error toggling item:', error);
      toast.error('Failed to toggle item');
    }
  };

  const handleDeleteItem = async (id) => {
    try {
      await mapItemsApi.delete(id);
      toast.success('Map item deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };

  const handleCreateNotification = async () => {
    setSavingNotif(true);
    try {
      await notificationsApi.create(notifForm);
      toast.success('Alert sent successfully');
      setNotifDialogOpen(false);
      setNotifForm({ title: '', message: '', priority: 'medium' });
      fetchData();
    } catch (error) {
      console.error('Error creating notification:', error);
      toast.error(getErrorMessage(error, 'Failed to send alert'));
    } finally {
      setSavingNotif(false);
    }
  };

  const handleDeleteNotification = async (id) => {
    try {
      await notificationsApi.delete(id);
      toast.success('Alert deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete alert');
    }
  };

  // User Management
  const handleCreateUser = async () => {
    if (!userForm.name || !userForm.username || (!editingUser && !userForm.password)) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (userForm.username.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }

    setSavingUser(true);
    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          name: userForm.name,
          username: userForm.username,
          email: userForm.email || null,
          role: userForm.role,
          department: userForm.department || null,
        };
        await usersApi.update(editingUser.id, updateData);
        toast.success('User updated successfully');
      } else {
        // Create new user
        await usersApi.create(userForm);
        toast.success('User created successfully');
      }
      setUserDialogOpen(false);
      resetUserForm();
      fetchData();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(getErrorMessage(error, 'Failed to save user'));
    } finally {
      setSavingUser(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      name: user.name,
      username: user.username,
      email: user.email || '',
      password: '',
      role: user.role,
      department: user.department || '',
    });
    setUserDialogOpen(true);
  };

  const resetUserForm = () => {
    setEditingUser(null);
    setUserForm({ name: '', username: '', email: '', password: '', role: 'personnel', department: '' });
  };

  const handleDeleteUser = async (id) => {
    try {
      await usersApi.delete(id);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(getErrorMessage(error, 'Failed to delete user'));
    }
  };

  // Department Management
  const handleCreateDepartment = async () => {
    if (!deptForm.name) {
      toast.error('Please enter a department name');
      return;
    }

    setSavingDept(true);
    try {
      await departmentsApi.create(deptForm);
      toast.success('Department created successfully');
      setDeptDialogOpen(false);
      setDeptForm({ name: '', description: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating department:', error);
      toast.error(getErrorMessage(error, 'Failed to create department'));
    } finally {
      setSavingDept(false);
    }
  };

  const handleDeleteDepartment = async (id) => {
    try {
      await departmentsApi.delete(id);
      toast.success('Department deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting department:', error);
      toast.error('Failed to delete department');
    }
  };

  // Callout Management
  const handleCreateCallout = async () => {
    if (!calloutForm.department_id || !calloutForm.title || !calloutForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSavingCallout(true);
    try {
      await calloutsApi.create(calloutForm);
      toast.success('Callout sent successfully! All department members will be notified.');
      setCalloutDialogOpen(false);
      setCalloutForm({ department_id: '', title: '', message: '', priority: 'high' });
      fetchData();
    } catch (error) {
      console.error('Error creating callout:', error);
      toast.error(getErrorMessage(error, 'Failed to send callout'));
    } finally {
      setSavingCallout(false);
    }
  };

  const handleDeleteCallout = async (id) => {
    try {
      await calloutsApi.delete(id);
      toast.success('Callout deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting callout:', error);
      toast.error('Failed to delete callout');
    }
  };

  const resetItemForm = () => {
    setEditingItem(null);
    setItemForm({
      title: '',
      description: '',
      item_type: 'road_closure',
      coordinates: '',
      radius: 200,
      is_active: true,
    });
  };

  const getItemIcon = (type) => {
    switch (type) {
      case 'road_closure': return <Ban className="w-4 h-4" />;
      case 'detour': return <Construction className="w-4 h-4" />;
      case 'parade_route': return <PartyPopper className="w-4 h-4" />;
      case 'closed_area': return <AlertTriangle className="w-4 h-4" />;
      default: return <Map className="w-4 h-4" />;
    }
  };

  const getItemColor = (type) => {
    switch (type) {
      case 'road_closure': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'detour': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'parade_route': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'closed_area': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-blue-500 text-white';
      case 'low': return 'bg-green-500 text-white';
      default: return 'bg-zinc-500 text-white';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'personnel': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading admin panel..." />;
  }

  return (
    <div className="min-h-screen bg-zinc-950 p-4 lg:p-6" data-testid="admin-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="font-headings text-3xl font-bold uppercase tracking-tight text-white">
            Admin Panel
          </h1>
          <p className="text-zinc-400 mt-1">
            Manage road closures, users, departments, and send public alerts
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            className="border-zinc-700 text-zinc-200 hover:text-white font-bold uppercase"
            onClick={() => window.open('/print-map', '_blank')}
            data-testid="admin-print-map-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print Map
          </Button>
          {/* Add Map Item Dialog */}
          <Dialog open={itemDialogOpen} onOpenChange={(open) => {
            setItemDialogOpen(open);
            if (!open) resetItemForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-red-500 hover:bg-red-600 text-white font-bold uppercase" data-testid="add-map-item-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Map Item
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-headings text-xl uppercase">
                  {editingItem ? 'Edit Map Item' : 'Add Map Item'}
                </DialogTitle>
                <DialogDescription className="text-zinc-400">
                  Add road closures, detours, parade routes, or closed areas to the map.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Title</Label>
                  <Input
                    value={itemForm.title}
                    onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                    placeholder="Main Street Closure"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="item-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Description</Label>
                  <Textarea
                    value={itemForm.description}
                    onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                    placeholder="Road closed due to construction"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="item-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Type</Label>
                  <Select
                    value={itemForm.item_type}
                    onValueChange={(value) => setItemForm({ ...itemForm, item_type: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="item-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="road_closure" className="text-white">Road Closure</SelectItem>
                      <SelectItem value="detour" className="text-white">Detour</SelectItem>
                      <SelectItem value="parade_route" className="text-white">Parade Route</SelectItem>
                      <SelectItem value="closed_area" className="text-white">Closed Area</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">
                    {itemForm.item_type === 'closed_area' ? 'Center Point' : 'Coordinates'}
                  </Label>
                  <Textarea
                    value={itemForm.coordinates}
                    onChange={(e) => setItemForm({ ...itemForm, coordinates: e.target.value })}
                    placeholder={itemForm.item_type === 'closed_area' ? '46.1268, -112.9536' : '46.1268, -112.9536; 46.1280, -112.9500; 46.1300, -112.9480'}
                    className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm min-h-[80px]"
                    data-testid="item-coordinates-input"
                  />
                  <div className="text-xs text-zinc-500 space-y-1">
                    {itemForm.item_type === 'closed_area' ? (
                      <>
                        <p><strong>Center:</strong> Enter a single lat, lng point for the area center</p>
                        <p><strong>Tip:</strong> Use Google Maps to find coordinates (right-click to see options)</p>
                      </>
                    ) : (
                      <>
                        <p><strong>Format:</strong> lat, lng; lat, lng; lat, lng</p>
                        <p><strong>Single point:</strong> 46.1268, -112.9536</p>
                        <p><strong>Line/Route:</strong> 46.1268, -112.9536; 46.1280, -112.9500</p>
                        <p><strong>Tip:</strong> Use Google Maps to find coordinates (right-click to see options)</p>
                      </>
                    )}
                  </div>
                </div>
                {itemForm.item_type === 'closed_area' && (
                  <div className="space-y-3" data-testid="item-radius-section">
                    <div className="flex items-center justify-between">
                      <Label className="text-zinc-300">Radius</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={10}
                          step={10}
                          value={itemForm.radius}
                          onChange={(e) => setItemForm({ ...itemForm, radius: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white w-24 h-8 text-right"
                          data-testid="item-radius-input"
                        />
                        <span className="text-xs text-zinc-400 w-8">m</span>
                      </div>
                    </div>
                    <Slider
                      value={[Math.min(Math.max(Number(itemForm.radius) || 0, 10), 3000)]}
                      min={10}
                      max={3000}
                      step={10}
                      onValueChange={(val) => setItemForm({ ...itemForm, radius: val[0] })}
                      data-testid="item-radius-slider"
                    />
                    <p className="text-xs text-zinc-500">
                      {(Number(itemForm.radius) || 0) >= 1000
                        ? `${((Number(itemForm.radius) || 0) / 1000).toFixed(2)} km`
                        : `${Math.round(Number(itemForm.radius) || 0)} m`} radius around the center point
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-300">Active</Label>
                  <Switch
                    checked={itemForm.is_active}
                    onCheckedChange={(checked) => setItemForm({ ...itemForm, is_active: checked })}
                    data-testid="item-active-switch"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setItemDialogOpen(false);
                    resetItemForm();
                  }}
                  className="border-zinc-700 text-zinc-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateItem}
                  disabled={savingItem || !itemForm.title || !itemForm.coordinates}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  data-testid="save-item-btn"
                >
                  {savingItem ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    editingItem ? 'Update' : 'Create'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Send Alert Dialog */}
          <Dialog open={notifDialogOpen} onOpenChange={setNotifDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:text-white font-bold uppercase" data-testid="send-alert-btn">
                <Bell className="w-4 h-4 mr-2" />
                Send Alert
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-headings text-xl uppercase">Send Public Alert</DialogTitle>
                <DialogDescription className="text-zinc-400">
                  Send an emergency notification to all users.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Title</Label>
                  <Input
                    value={notifForm.title}
                    onChange={(e) => setNotifForm({ ...notifForm, title: e.target.value })}
                    placeholder="Emergency Alert"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="notif-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Message</Label>
                  <Textarea
                    value={notifForm.message}
                    onChange={(e) => setNotifForm({ ...notifForm, message: e.target.value })}
                    placeholder="Describe the emergency or alert..."
                    className="bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
                    data-testid="notif-message-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Priority</Label>
                  <Select
                    value={notifForm.priority}
                    onValueChange={(value) => setNotifForm({ ...notifForm, priority: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="notif-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="low" className="text-white">Low</SelectItem>
                      <SelectItem value="medium" className="text-white">Medium</SelectItem>
                      <SelectItem value="high" className="text-white">High</SelectItem>
                      <SelectItem value="critical" className="text-white">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setNotifDialogOpen(false)}
                  className="border-zinc-700 text-zinc-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateNotification}
                  disabled={savingNotif || !notifForm.title || !notifForm.message}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  data-testid="send-notif-btn"
                >
                  {savingNotif ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Alert
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-zinc-900 border border-zinc-800 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="map-items" className="data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-map-items">
            <Map className="w-4 h-4 mr-2" />
            Map Items ({mapItems.length})
          </TabsTrigger>
          <TabsTrigger value="alerts" className="data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-alerts">
            <Bell className="w-4 h-4 mr-2" />
            Alerts ({notifications.length})
          </TabsTrigger>
          <TabsTrigger value="callouts" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-callouts">
            <Megaphone className="w-4 h-4 mr-2" />
            Callouts ({callouts.length})
          </TabsTrigger>
          {canManageUsers && (
            <>
              <TabsTrigger value="users" className="data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-users">
                <Users className="w-4 h-4 mr-2" />
                Users ({users.length})
              </TabsTrigger>
              <TabsTrigger value="departments" className="data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-departments">
                <Building2 className="w-4 h-4 mr-2" />
                Departments ({departments.length})
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Map Items Tab */}
        <TabsContent value="map-items" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Map Preview */}
            <Card className="bg-zinc-900 border-zinc-800 h-[400px]">
              <CardHeader className="pb-2">
                <CardTitle className="font-headings text-lg uppercase tracking-wide text-white">
                  Map Preview
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2 h-[calc(100%-60px)]">
                <EmergencyMap items={mapItems.filter(i => i.is_active)} height="100%" />
              </CardContent>
            </Card>

            {/* Items List */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="font-headings text-lg uppercase tracking-wide text-white">
                  All Map Items
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[320px]">
                  {mapItems.length === 0 ? (
                    <EmptyState 
                      type="map-items" 
                      action={() => setItemDialogOpen(true)}
                      actionLabel="Add First Item"
                    />
                  ) : (
                    <div className="space-y-3">
                      {mapItems.map((item) => (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50 border ${
                            item.is_active ? 'border-zinc-700' : 'border-zinc-800 opacity-60'
                          }`}
                        >
                          <div className={`p-2 rounded-lg ${getItemColor(item.item_type)}`}>
                            {getItemIcon(item.item_type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-medium text-white truncate">{item.title}</h4>
                              {!item.is_active && (
                                <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-500">
                                  Inactive
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-zinc-500 truncate">{item.description}</p>
                            <p className="text-xs text-zinc-600">
                              {item.item_type === 'closed_area' && item.radius
                                ? `${item.radius >= 1000 ? `${(item.radius / 1000).toFixed(2)} km` : `${Math.round(item.radius)} m`} radius`
                                : `${item.coordinates?.length || 0} point(s)`}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleItem(item)}
                              className={item.is_active ? 'text-green-400 hover:text-green-300' : 'text-zinc-500 hover:text-zinc-400'}
                              data-testid={`toggle-item-${item.id}`}
                            >
                              <Power className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditItem(item)}
                              className="text-zinc-400 hover:text-white"
                              data-testid={`edit-item-${item.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-400 hover:text-red-300"
                                  data-testid={`delete-item-${item.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-white">Delete Map Item</AlertDialogTitle>
                                  <AlertDialogDescription className="text-zinc-400">
                                    Are you sure you want to delete "{item.title}"? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="border-zinc-700 text-zinc-300">Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteItem(item.id)}
                                    className="bg-red-500 hover:bg-red-600 text-white"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="font-headings text-lg uppercase tracking-wide text-white">
                All Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {notifications.length === 0 ? (
                  <EmptyState 
                    type="alerts" 
                    action={() => setNotifDialogOpen(true)}
                    actionLabel="Send First Alert"
                  />
                ) : (
                  <div className="space-y-3">
                    {notifications.map((notif) => (
                      <motion.div
                        key={notif.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={getPriorityColor(notif.priority)}>
                                {notif.priority}
                              </Badge>
                              <TimeAgo date={notif.created_at} className="text-xs" />
                            </div>
                            <h4 className="text-white font-medium">{notif.title}</h4>
                            <p className="text-zinc-400 text-sm mt-1">{notif.message}</p>
                          </div>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-400 hover:text-red-300 flex-shrink-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-white">Delete Alert</AlertDialogTitle>
                                <AlertDialogDescription className="text-zinc-400">
                                  Are you sure you want to delete this alert? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="border-zinc-700 text-zinc-300">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteNotification(notif.id)}
                                  className="bg-red-500 hover:bg-red-600 text-white"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Callouts Tab */}
        <TabsContent value="callouts" className="space-y-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="font-headings text-lg uppercase tracking-wide text-white flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-orange-400" />
                Department Callouts
              </CardTitle>
              <Dialog open={calloutDialogOpen} onOpenChange={setCalloutDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="send-callout-btn">
                    <Megaphone className="w-4 h-4 mr-2" />
                    Send Callout
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-headings text-xl uppercase">Send Department Callout</DialogTitle>
                    <DialogDescription className="text-zinc-400">
                      Alert all members of a department with an urgent message.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Department *</Label>
                      <Select
                        value={calloutForm.department_id}
                        onValueChange={(value) => setCalloutForm({ ...calloutForm, department_id: value })}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="callout-dept-select">
                          <SelectValue placeholder="Select department to alert" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {departments.map((dept) => (
                            <SelectItem key={dept.id} value={dept.id} className="text-white">
                              {dept.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Title *</Label>
                      <Input
                        value={calloutForm.title}
                        onChange={(e) => setCalloutForm({ ...calloutForm, title: e.target.value })}
                        placeholder="Emergency Response Needed"
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="callout-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Message *</Label>
                      <Textarea
                        value={calloutForm.message}
                        onChange={(e) => setCalloutForm({ ...calloutForm, message: e.target.value })}
                        placeholder="Brief description of the situation..."
                        className="bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
                        data-testid="callout-message-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Priority</Label>
                      <Select
                        value={calloutForm.priority}
                        onValueChange={(value) => setCalloutForm({ ...calloutForm, priority: value })}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="callout-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          <SelectItem value="critical" className="text-red-400">Critical - Immediate Response</SelectItem>
                          <SelectItem value="high" className="text-orange-400">High - Urgent</SelectItem>
                          <SelectItem value="medium" className="text-blue-400">Medium - Standard</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setCalloutDialogOpen(false)}
                      className="border-zinc-700 text-zinc-300"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreateCallout}
                      disabled={savingCallout || !calloutForm.department_id || !calloutForm.title || !calloutForm.message}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="send-callout-confirm-btn"
                    >
                      {savingCallout ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Megaphone className="w-4 h-4 mr-2" />
                          Send Callout
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {callouts.length === 0 ? (
                  <EmptyState 
                    type="default"
                    title="No Callouts"
                    description="Send a callout to alert department members about urgent situations."
                    action={() => setCalloutDialogOpen(true)}
                    actionLabel="Send First Callout"
                    icon={Megaphone}
                  />
                ) : (
                  <div className="space-y-4">
                    {callouts.map((callout) => (
                      <motion.div
                        key={callout.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`p-4 rounded-lg bg-zinc-800/50 border border-l-4 ${
                          callout.priority === 'critical' ? 'border-l-red-500 border-zinc-700' :
                          callout.priority === 'high' ? 'border-l-orange-500 border-zinc-700' :
                          'border-l-blue-500 border-zinc-700'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={
                                callout.priority === 'critical' ? 'bg-red-500 text-white' :
                                callout.priority === 'high' ? 'bg-orange-500 text-white' :
                                'bg-blue-500 text-white'
                              }>
                                {callout.priority}
                              </Badge>
                              <Badge variant="outline" className="text-zinc-400 border-zinc-600">
                                <Building2 className="w-3 h-3 mr-1" />
                                {callout.department_name}
                              </Badge>
                              <TimeAgo date={callout.created_at} className="text-xs" />
                            </div>
                            <h4 className="text-white font-medium text-lg">{callout.title}</h4>
                            <p className="text-zinc-400 text-sm mt-1">{callout.message}</p>
                            <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                              <span>By: {callout.created_by_name}</span>
                              <span className="flex items-center gap-1">
                                <Check className="w-3 h-3" />
                                {callout.acknowledged_by?.length || 0} acknowledged
                              </span>
                            </div>
                          </div>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-400 hover:text-red-300 flex-shrink-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-white">Delete Callout</AlertDialogTitle>
                                <AlertDialogDescription className="text-zinc-400">
                                  Are you sure you want to delete this callout? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="border-zinc-700 text-zinc-300">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteCallout(callout.id)}
                                  className="bg-red-500 hover:bg-red-600 text-white"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab - Admin Only */}
        {canManageUsers && (
        <TabsContent value="users" className="space-y-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="font-headings text-lg uppercase tracking-wide text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                User Management
              </CardTitle>
              <Dialog open={userDialogOpen} onOpenChange={(open) => {
                setUserDialogOpen(open);
                if (!open) resetUserForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="add-user-btn" onClick={() => resetUserForm()}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-headings text-xl uppercase">
                      {editingUser ? 'Edit User' : 'Create User'}
                    </DialogTitle>
                    <DialogDescription className="text-zinc-400">
                      {editingUser ? 'Update user information.' : 'Create a new personnel or admin account.'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Full Name *</Label>
                      <Input
                        value={userForm.name}
                        onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                        placeholder="John Doe"
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="user-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Username *</Label>
                      <Input
                        type="text"
                        value={userForm.username}
                        onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                        placeholder="johndoe"
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="user-username-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Email (Optional)</Label>
                      <Input
                        type="email"
                        value={userForm.email}
                        onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                        placeholder="user@example.com"
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="user-email-input"
                      />
                    </div>
                    {!editingUser && (
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Password *</Label>
                        <Input
                          type="password"
                          value={userForm.password}
                          onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                          placeholder="••••••••"
                          className="bg-zinc-800 border-zinc-700 text-white"
                          data-testid="user-password-input"
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Role</Label>
                      <Select
                        value={userForm.role}
                        onValueChange={(value) => setUserForm({ ...userForm, role: value })}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="user-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          <SelectItem value="personnel" className="text-white">Personnel</SelectItem>
                          <SelectItem value="admin" className="text-white">Administrator</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Department</Label>
                      <Select
                        value={userForm.department || '__none__'}
                        onValueChange={(value) => setUserForm({ ...userForm, department: value === '__none__' ? '' : value })}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="user-dept-select">
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          <SelectItem value="__none__" className="text-zinc-400">No Department</SelectItem>
                          {departments.map((dept) => (
                            <SelectItem key={dept.id} value={dept.name} className="text-white">
                              {dept.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setUserDialogOpen(false);
                        resetUserForm();
                      }}
                      className="border-zinc-700 text-zinc-300"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreateUser}
                      disabled={savingUser || !userForm.name || !userForm.username || (!editingUser && !userForm.password)}
                      className="bg-blue-500 hover:bg-blue-600 text-white"
                      data-testid="create-user-btn"
                    >
                      {savingUser ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {editingUser ? 'Saving...' : 'Creating...'}
                        </>
                      ) : (
                        editingUser ? 'Save Changes' : 'Create User'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                {users.length === 0 ? (
                  <EmptyState 
                    type="default"
                    title="No Users Found"
                    description="Create user accounts for your emergency personnel."
                    action={() => setUserDialogOpen(true)}
                    actionLabel="Add First User"
                  />
                ) : (
                  <div className="space-y-3">
                    {users.map((u) => (
                      <motion.div
                        key={u.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-4 p-4 rounded-lg bg-zinc-800/50 border border-zinc-700"
                      >
                        <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
                          {u.role === 'admin' ? (
                            <Shield className="w-5 h-5 text-red-400" />
                          ) : u.role === 'personnel' ? (
                            <User className="w-5 h-5 text-blue-400" />
                          ) : (
                            <User className="w-5 h-5 text-zinc-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-white">{u.name}</h4>
                            <Badge variant="outline" className={getRoleBadgeColor(u.role)}>
                              {u.role}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <User className="w-3 h-3 text-zinc-500" />
                            <span className="text-xs text-zinc-500">@{u.username}</span>
                            {u.department && (
                              <>
                                <span className="text-zinc-600">•</span>
                                <Building2 className="w-3 h-3 text-zinc-500" />
                                <span className="text-xs text-zinc-500">{u.department}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-blue-400 hover:text-blue-300"
                            onClick={() => handleEditUser(u)}
                            data-testid={`edit-user-${u.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-400 hover:text-red-300"
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-white">Delete User</AlertDialogTitle>
                                <AlertDialogDescription className="text-zinc-400">
                                  Are you sure you want to delete "{u.name}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="border-zinc-700 text-zinc-300">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteUser(u.id)}
                                  className="bg-red-500 hover:bg-red-600 text-white"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Departments Tab - Admin Only */}
        {canManageUsers && (
        <TabsContent value="departments" className="space-y-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="font-headings text-lg uppercase tracking-wide text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-orange-400" />
                Department Management
              </CardTitle>
              <Dialog open={deptDialogOpen} onOpenChange={setDeptDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="add-dept-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Department
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-headings text-xl uppercase">Create Department</DialogTitle>
                    <DialogDescription className="text-zinc-400">
                      Add a new department for organizing personnel.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Department Name</Label>
                      <Input
                        value={deptForm.name}
                        onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })}
                        placeholder="Fire Department"
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="dept-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Description (Optional)</Label>
                      <Textarea
                        value={deptForm.description}
                        onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })}
                        placeholder="Department description..."
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="dept-description-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setDeptDialogOpen(false)}
                      className="border-zinc-700 text-zinc-300"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreateDepartment}
                      disabled={savingDept || !deptForm.name}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="create-dept-btn"
                    >
                      {savingDept ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create Department'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                {departments.length === 0 ? (
                  <EmptyState 
                    type="default"
                    title="No Departments"
                    description="Create departments to organize your personnel."
                    action={() => setDeptDialogOpen(true)}
                    actionLabel="Add First Department"
                    icon={Building2}
                  />
                ) : (
                  <div className="space-y-3">
                    {departments.map((dept) => (
                      <motion.div
                        key={dept.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-4 p-4 rounded-lg bg-zinc-800/50 border border-zinc-700"
                      >
                        <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-orange-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-white">{dept.name}</h4>
                          {dept.description && (
                            <p className="text-xs text-zinc-500 mt-1">{dept.description}</p>
                          )}
                          <p className="text-xs text-zinc-600 mt-1">
                            {users.filter(u => u.department === dept.name).length} members
                          </p>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-400 hover:text-red-300"
                              data-testid={`delete-dept-${dept.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-white">Delete Department</AlertDialogTitle>
                              <AlertDialogDescription className="text-zinc-400">
                                Are you sure you want to delete "{dept.name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="border-zinc-700 text-zinc-300">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteDepartment(dept.id)}
                                className="bg-red-500 hover:bg-red-600 text-white"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </motion.div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default AdminPage;
